import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import Select from 'react-select';
import { isFieldValueFilled } from '../../hooks';
import { FloatingLabel } from '../basic/FloatingLabel';
import { useWindowSizeContext } from '../../context/page/WindowSizeContext';
// Custom filter function that prioritizes prefix matches and improves relevance
const customFilter = (option, inputValue) => {
    if (!inputValue)
        return true;
    const input = inputValue.toLowerCase().trim();
    // Helper function to safely convert to string
    const safeToString = (value) => {
        if (typeof value === 'string')
            return value;
        if (typeof value === 'number')
            return value.toString();
        if (React.isValidElement(value)) {
            // For JSX elements, try to extract text content or use a fallback
            return '';
        }
        return value ? String(value) : '';
    };
    const label = safeToString(option.label || '').toLowerCase();
    const value = safeToString(option.value || '').toLowerCase();
    const rawValue = safeToString(option.raw_value || '').toLowerCase();
    // Check all possible text fields for matches
    const searchableText = `${label} ${value} ${rawValue}`;
    // Exact prefix match (highest priority) - only match if input is at the start
    if (label.startsWith(input) || value.startsWith(input) || rawValue.startsWith(input)) {
        return true;
    }
    // Word boundary prefix match (high priority) - only match if input starts a word
    const words = searchableText.split(/\s+/);
    for (const word of words) {
        if (word.startsWith(input)) {
            return true;
        }
    }
    // Remove the general contains match - only allow prefix and word boundary matches
    // This prevents "Arizona" from matching when typing "n"
    return false;
};
export const SelectDropdownInput = ({ name, options, value, label, placeholder, error, onblur, onchange, prefix, disabled, controlledLabel, menuPortalTargetId, onLabelStateChange, isSearchable, defaultValue, isMulti, style, filterOption, menuPlacement, openMenuOnFocus = true, onKeyDown, }) => {
    const [selectValue, setSelectValue] = useState(value);
    const lastSelectedValueRef = useRef(undefined);
    useEffect(() => {
        setSelectValue(value);
        lastSelectedValueRef.current = value;
    }, [value]);
    const isFilledValue = (val) => isMulti
        ? isFieldValueFilled(val && val.length > 0 ? val[0] : null)
        : isFieldValueFilled(val);
    const [floatingLabelState, setFloatingLabelState] = useState({
        isFilled: isFilledValue(selectValue),
    });
    const updateFloatingLabelState = useCallback((state) => {
        setFloatingLabelState(state);
        if (onLabelStateChange) {
            onLabelStateChange(state);
        }
    }, [onLabelStateChange]);
    useEffect(() => {
        if (!controlledLabel) {
            updateFloatingLabelState({
                isFilled: isFilledValue(selectValue),
                hasFocus: floatingLabelState.hasFocus,
            });
        }
    }, [selectValue, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);
    useEffect(() => {
        if (defaultValue) {
            setSelectValue(defaultValue);
        }
    }, []);
    const selectOptions = useMemo(() => _.map(options, (o) => {
        if (typeof o === 'string') {
            return {
                label: o,
                raw_value: o,
                value: o,
            };
        }
        else {
            return Object.assign({}, o, { label: o.label || o.value, raw_value: o.raw_value || o.value });
        }
    }), [options]);
    const selectValues = useMemo(() => _.map(_.isArray(selectValue) ? selectValue : !_.isNil(selectValue) ? [selectValue] : [], v => {
        // find selected option by matching raw value
        const option = _.find(selectOptions, o => o.raw_value === v);
        return Object.assign({}, option, { value: option ? option.value : v, label: option ? option.label : v, raw_value: option ? option.raw_value : v });
    }), [selectValue, selectOptions]);
    const menuPortalTarget = useMemo(() => menuPortalTargetId && document.getElementById(menuPortalTargetId), [
        menuPortalTargetId,
    ]);
    const width = useWindowSizeContext().width;
    const narrowPage = useMemo(() => width < 500, [width]);
    const smallScreen = width <= 768;
    /* When not searchable, keep inputValue empty so SingleValue stays visible when focused/menu open */
    const isActuallySearchable = !narrowPage && isSearchable;
    const selectInputProps = useMemo(() => {
        if (!isActuallySearchable) {
            return { inputValue: '', onInputChange: () => { } };
        }
        return {};
    }, [isActuallySearchable]);
    /* Vertical centering: fixed height + alignItems stretch so value-container fills control;
       react-select's singleValue uses position:absolute + top:50% + translateY(-50%). */
    const controlHeight = smallScreen ? 56 : 48;
    const selectStyles = useMemo(() => (Object.assign({}, (menuPortalTargetId && {
        menuPortal: (provided) => (Object.assign({}, provided, { zIndex: 9999 })),
    }), { control: (provided) => (Object.assign({}, provided, { minHeight: controlHeight }, (!isMulti && { height: controlHeight }), { paddingTop: 0, paddingBottom: 0, alignItems: 'stretch' })), valueContainer: (provided) => (Object.assign({}, provided, { height: isMulti ? '100%' : controlHeight, alignSelf: 'stretch', paddingTop: 0, paddingBottom: 0, position: 'relative' })) })), [menuPortalTargetId, controlHeight, isMulti]);
    const onFocus = (e) => {
        updateFloatingLabelState({ isFilled: true, hasFocus: true });
    };
    const onBlur = (e) => {
        if (onblur) {
            onblur(e);
        }
        // Use lastSelectedValueRef when controlled: onBlur can fire before the value prop updates,
        // and selectValue may still be stale. This prevents the placeholder from reappearing.
        const valueForFilled = value != null ? value : lastSelectedValueRef.current != null ? lastSelectedValueRef.current : selectValue;
        updateFloatingLabelState({
            isFilled: isFilledValue(valueForFilled),
            hasFocus: false,
        });
    };
    const onChange = (o) => {
        let values = _.map(_.isArray(o) ? o : [o], v => v.raw_value);
        if (!isMulti)
            values = _.first(values);
        if (onchange) {
            lastSelectedValueRef.current = values;
            onchange(undefined, values);
        }
        else {
            setSelectValue(values);
        }
        updateFloatingLabelState({
            isFilled: isFilledValue(values),
            hasFocus: floatingLabelState.hasFocus,
        });
    };
    return (React.createElement(FloatingLabel, { id: name, className: error ? 'invalid' : '', label: placeholder || label || name, prefix: prefix, state: floatingLabelState, disabled: disabled, style: style },
        React.createElement("div", { onKeyDown: onKeyDown, style: { display: 'contents' } },
            React.createElement(Select, Object.assign({}, (isMulti && { isMulti }), selectInputProps, { inputId: name, name: name, className: "basic-multi-select", classNamePrefix: "select", value: selectValues, onFocus: onFocus, onBlur: onBlur, onChange: onChange, options: selectOptions, placeholder: "", isSearchable: !narrowPage && isSearchable, isDisabled: disabled, menuPortalTarget: menuPortalTarget, styles: selectStyles, selectProps: { autoComplete: 'nope' }, filterOption: filterOption || (isSearchable ? customFilter : undefined), menuPlacement: menuPlacement, openMenuOnFocus: openMenuOnFocus, controlShouldRenderValue: true })))));
};
//# sourceMappingURL=SelectDropdown.js.map